import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { FaSearch } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { RootState } from "../../redux/store";
import {
  getAccountOfficerAssignedCustomersWithRisk,
  getAccountOfficerDashboardStats,
} from "../../redux/thunk";
import LoadingSpinner from "../../components/elements/LoadingSpinner";

const AccountManagement = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = searchParams.get("filter");
  const accountType = searchParams.get("accountType");
  const dispatch = useAppDispatch();
  const { token } = useAppSelector((store: RootState) => store.auth);
  const { dashboardStats, dashboardStatsStatus } = useAppSelector(
    (store: RootState) => store.accountOfficer
  );
  const {
    assignedCustomers,
    assignedCustomersStatus,
  } = useAppSelector((store: RootState) => store.accountOfficer);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const activeTab: "all" | "flexible" | "outright" =
    accountType === "flexible"
      ? "flexible"
      : accountType === "outright"
        ? "outright"
        : "all";

  useEffect(() => {
    dispatch(getAccountOfficerDashboardStats({}));
  }, [dispatch]);

  // Fetch ALL customers in one call — filtering, counting, and pagination
  // are handled entirely client-side because the API does not reliably filter
  // by accountType in its total/data response.
  useEffect(() => {
    if (!token) return;
    dispatch(
      getAccountOfficerAssignedCustomersWithRisk({
        page: 1,
        limit: 9999,
        filter: filter || undefined,
      })
    );
  }, [dispatch, token, filter]);

  // Reset to page 1 when search term or tab changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, accountType]);

  // ── Client-side counts (derived from the full dataset) ──
  const allCount = assignedCustomers.length;
  const flexibleCount = useMemo(
    () => assignedCustomers.filter((c) => c.accountType?.toLowerCase() === "flexible").length,
    [assignedCustomers],
  );
  const outrightCount = useMemo(
    () => assignedCustomers.filter((c) => c.accountType?.toLowerCase() === "outright").length,
    [assignedCustomers],
  );

  // ── Filter chain: accountType → search ──
  const accountTypeFiltered = useMemo(
    () =>
      activeTab === "all"
        ? assignedCustomers
        : assignedCustomers.filter(
            (c) => c.accountType?.toLowerCase() === activeTab,
          ),
    [assignedCustomers, activeTab],
  );

  const searchFiltered = useMemo(
    () =>
      accountTypeFiltered.filter(
        (c) =>
          c.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          c.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          c.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          c.phoneNumber?.toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    [accountTypeFiltered, searchTerm],
  );

  // ── Client-side pagination ──
  const totalFiltered = searchFiltered.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / itemsPerPage));
  const paginatedCustomers = useMemo(
    () =>
      searchFiltered.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage,
      ),
    [searchFiltered, currentPage, itemsPerPage],
  );

  const formatNaira = (value: number) => {
    if (!value || value === 0) return "0";
    return `N${Number(value).toLocaleString()}`;
  };

  const StatsCard = ({ title, value, subtitle, subtitleColor }: any) => (
    <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex-1 min-w-[240px]">
      <div className="flex justify-between items-start mb-2">
        <p className="text-gray-500 text-xs font-medium uppercase tracking-wider">
          {title}
          {subtitle ? (
            <>
              {" "}
              <span className={`text-[10px] lowercase ${subtitleColor}`}>
                {subtitle}
              </span>
            </>
          ) : null}
        </p>
      </div>
      <h3 className="text-3xl font-bold text-gray-900">{value}</h3>
    </div>
  );

  const startIndex = totalFiltered === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endIndex =
    totalFiltered === 0
      ? 0
      : (currentPage - 1) * itemsPerPage + paginatedCustomers.length;

  const setTab = (next: "all" | "flexible" | "outright") => {
    setCurrentPage(1);
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (next === "all") {
        p.delete("accountType");
      } else {
        p.set("accountType", next);
        // Ensure we don't combine product segmentation with other dashboard filters.
        p.delete("filter");
      }
      return p;
    });
  };

  const TabButton = ({
    label,
    count,
    isActive,
    onClick,
  }: {
    label: string;
    count: number | null;
    isActive: boolean;
    onClick: () => void;
  }) => (
    <button
      type="button"
      onClick={onClick}
      className={`pb-3 border-b-2 transition-colors font-bold text-sm ${
        isActive
          ? "border-green-500 text-gray-900"
          : "border-transparent text-gray-400 hover:text-gray-700"
      }`}
    >
      <span className="flex items-center gap-2">
        <span>{label}</span>
        <span
          className={`text-xs font-extrabold ${
            isActive ? "text-green-600" : "text-gray-300"
          }`}
        >
          {count === null ? "—" : count.toLocaleString()}
        </span>
      </span>
    </button>
  );

  return (
    <div className="w-full bg-gray-50/30 min-h-screen">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Account Management</h1>
        <p className="text-gray-500 text-sm">
          Manage platform news and announcements
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatsCard
          title="My Customers"
          value={
            dashboardStatsStatus === "loading"
              ? "..."
              : (dashboardStats?.totalAssignedCustomers || 0).toLocaleString()
          }
        />
        <StatsCard
          title="Total Revenue"
          value={
            dashboardStatsStatus === "loading"
              ? "..."
              : formatNaira(dashboardStats?.revenue || 0)
          }
        />
        <StatsCard
          title="Total Orders"
          value={
            dashboardStatsStatus === "loading"
              ? "..."
              : (dashboardStats?.totalOrders || 0).toLocaleString()
          }
        />
        <StatsCard
          title="Avg Order Value"
          value={
            dashboardStatsStatus === "loading"
              ? "..."
              : formatNaira(
                  (dashboardStats?.revenue || 0) /
                    (dashboardStats?.totalOrders || 1),
                )
          }
        />
      </div>

      <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-8">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-8">
            <div>
              <h2 className="text-lg font-bold text-gray-900">My Customers</h2>
              <p className="text-xs text-gray-400 font-medium">
                Customers assigned to you
              </p>
            </div>

            <div className="flex items-end gap-8 border-b border-gray-100">
              <TabButton
                label="My Customers"
                count={
                  assignedCustomersStatus === "loading"
                    ? null
                    : allCount
                }
                isActive={activeTab === "all"}
                onClick={() => setTab("all")}
              />
              <TabButton
                label="Flexible Users"
                count={
                  assignedCustomersStatus === "loading"
                    ? null
                    : flexibleCount
                }
                isActive={activeTab === "flexible"}
                onClick={() => setTab("flexible")}
              />
              <TabButton
                label="Outright Users"
                count={
                  assignedCustomersStatus === "loading"
                    ? null
                    : outrightCount
                }
                isActive={activeTab === "outright"}
                onClick={() => setTab("outright")}
              />
            </div>
          </div>

          <div className="relative mb-10">
            <input
              type="text"
              placeholder="Search by customer name, email or account officer..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-4 bg-gray-50 border-none rounded-2xl text-sm focus:ring-2 focus:ring-green-500 transition-all font-medium"
            />
            <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 text-lg" />
          </div>

          <div className="overflow-x-auto overflow-y-hidden">
            <table className="w-full text-left min-w-[1050px]">
              <thead className="text-gray-400 text-[11px] font-bold uppercase tracking-widest border-b border-gray-50">
                <tr>
                  <th className="px-4 pb-6">S/N</th>
                  <th className="px-4 pb-6">Name</th>
                  <th className="px-4 pb-6">E-Mail Address</th>
                  <th className="px-4 pb-6">Phone Number</th>
                  <th className="px-4 pb-6">Total Spend</th>
                  <th className="px-4 pb-6 text-center">Account Type</th>
                  <th className="px-4 pb-6 text-center">Status</th>
                  <th className="px-4 pb-6 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {assignedCustomersStatus === "loading" ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-20">
                      <div className="flex justify-center">
                        <LoadingSpinner />
                      </div>
                    </td>
                  </tr>
                ) : paginatedCustomers.length > 0 ? (
                  paginatedCustomers.map((customer, index) => {
                    const isActive =
                      typeof customer.status === "string" &&
                      customer.status.toLowerCase() === "active";
                    return (
                      <tr
                        key={customer.customerId}
                        className="hover:bg-gray-50/80 transition-all group"
                      >
                        <td className="px-4 py-6 text-sm text-gray-500 font-medium">
                          {(currentPage - 1) * itemsPerPage + index + 1}
                        </td>
                        <td className="px-4 py-6 text-sm font-bold text-gray-900 group-hover:text-green-700">
                          {customer.firstName} {customer.lastName}
                        </td>
                        <td className="px-4 py-6 text-sm text-gray-500 font-medium">
                          {customer.email}
                        </td>
                        <td className="px-4 py-6 text-sm text-gray-500 font-medium">
                          {customer.phoneNumber}
                        </td>
                        <td className="px-4 py-6 text-sm font-bold text-gray-700">
                          {formatNaira(customer.totalSpend || 0)}
                        </td>
                        <td className="px-4 py-6 text-center">
                          <span
                            className={`px-3 py-1.5 rounded-xl text-[10px] font-extrabold capitalize shadow-sm ${
                              customer.accountType?.toLowerCase() === "flexible"
                                ? "bg-blue-50 text-blue-700 border border-blue-100"
                                : customer.accountType?.toLowerCase() === "outright"
                                  ? "bg-purple-50 text-purple-700 border border-purple-100"
                                  : "bg-gray-50 text-gray-500 border border-gray-100"
                            }`}
                          >
                            {customer.accountType || "N/A"}
                          </span>
                        </td>
                        <td className="px-4 py-6 text-center">
                          <span
                            className={`px-4 py-1.5 rounded-xl text-[10px] font-extrabold shadow-sm ${isActive ? "bg-green-600 text-white" : "bg-red-500 text-white"}`}
                          >
                            {customer.status}
                          </span>
                        </td>
                        <td className="px-4 py-6 text-right">
                          <button
                            onClick={() =>
                              navigate(
                                `/sales-dashboard/account-management/${customer.customerId}`,
                              )
                            }
                            className="text-green-600 font-extrabold text-sm hover:underline hover:scale-105 transition-transform"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-4 py-10 text-center text-gray-400 font-medium"
                    >
                      No customers found matching your search.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="p-8 bg-gray-50/50 border-t border-gray-100 flex items-center justify-between">
          <p className="text-sm text-gray-400 font-medium">
            Showing{" "}
            <span className="text-gray-900 font-bold">
              {startIndex}
            </span>{" "}
            -{" "}
            <span className="text-gray-900 font-bold">
              {endIndex}
            </span>{" "}
            of{" "}
            <span className="text-gray-900 font-bold">
              {totalFiltered}
            </span>{" "}
            users
          </p>
          <div className="flex gap-3">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((prev) => prev - 1)}
              className="px-6 py-2.5 border border-gray-200 bg-white rounded-xl text-sm font-bold text-gray-600 disabled:opacity-50 hover:bg-gray-50 transition-all shadow-sm"
            >
              Previous
            </button>
            <button
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage((prev) => prev + 1)}
              className="px-8 py-2.5 bg-white border border-gray-200 rounded-xl text-sm font-bold text-gray-900 transition-all hover:bg-gray-100 shadow-sm"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountManagement;
